import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowDownUp, Settings } from 'lucide-react';
import { formatUnits, parseUnits, type Address } from 'viem';
import { useAccount, useReadContract, useReadContracts, useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import { TOKENS, type MarketPair } from '../data/mockMarket';
import { getDeployment, getTokenAddress, resolveSwapPath } from '../web3/contracts';
import { ERC20_ABI, ROUTER_ABI } from '../web3/abis';
import { TARGET_CHAIN } from '../web3/config';
import { useTokenBalance } from '../web3/useTokenBalance';
import { useTokenDecimals } from '../web3/useTokenDecimals';
import { useFactoryPairs } from '../web3/useFactoryPairs';
import { logTransaction } from '../web3/logTransaction';
import TokenIcon from './TokenIcon';
import TrustBar from './TrustBar';
import TransactionTimeout from './TransactionTimeout';

const DEADLINE_SECONDS = 20 * 60;

function safeParseUnits(value: string, decimals = 18): bigint | null {
  if (!value || Number.isNaN(Number(value)) || Number(value) <= 0) return null;
  try {
    return parseUnits(value as `${number}`, decimals);
  } catch {
    return null;
  }
}

export default function SwapPanel({
  pair,
  initialFrom,
  initialTo,
}: {
  pair: MarketPair;
  initialFrom?: string;
  initialTo?: string;
}) {
  const [fromToken, setFromToken] = useState(initialFrom ?? pair.quote);
  const [toToken, setToToken] = useState(initialTo ?? pair.base);
  const [fromAmount, setFromAmount] = useState('');
  const [slippage, setSlippage] = useState(0.5);
  const [customSlippage, setCustomSlippage] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [txError, setTxError] = useState<string | null>(null);
  // user must explicitly acknowledge a high (>5%) price-impact trade before it can execute
  const [ackHighImpact, setAckHighImpact] = useState(false);

  const fromDecimals = useTokenDecimals(fromToken);
  const toDecimals = useTokenDecimals(toToken);

  const { address, isConnected, chainId } = useAccount();
  const onCorrectChain = chainId === TARGET_CHAIN.id;
  // reads must target the chain we deployed to even before a wallet is connected,
  // otherwise wagmi falls back to whichever chain it resolves as "current"
  const readChainId = chainId ?? TARGET_CHAIN.id;

  const deployment = getDeployment(readChainId);
  const fromAddress = getTokenAddress(fromToken, readChainId);
  const factoryPairs = useFactoryPairs(readChainId);
  const routeCandidates = useMemo(
    () => resolveSwapPath(fromToken, toToken, factoryPairs.pairs, readChainId),
    [factoryPairs.pairs, fromToken, toToken, readChainId],
  );

  const amountInWei = safeParseUnits(fromAmount, fromDecimals);

  const isFromNative = fromToken === 'BNB';
  const isToNative = toToken === 'BNB';

  const { formatted: fromBalance } = useTokenBalance(fromToken);
  const { formatted: toBalance } = useTokenBalance(toToken);

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: fromAddress,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address && deployment ? [address, deployment.router] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(address && deployment && fromAddress && !isFromNative) },
  });

  const quoteContracts = useMemo(
    () =>
      amountInWei && deployment
        ? routeCandidates.map(
            (candidate) =>
              ({
                address: deployment.router,
                abi: ROUTER_ABI,
                functionName: 'getAmountsOut',
                args: [amountInWei, candidate],
                chainId: readChainId,
              }) as const,
          )
        : [],
    [amountInWei, deployment, readChainId, routeCandidates],
  );
  const { data: routeQuotes } = useReadContracts({
    contracts: quoteContracts,
    query: { enabled: quoteContracts.length > 0 },
  });

  const bestQuote = useMemo(() => {
    let best: { path: Address[]; amounts: readonly bigint[] } | undefined;
    routeQuotes?.forEach((quote, index) => {
      if (quote.status !== 'success') return;
      const amounts = quote.result as readonly bigint[];
      const output = amounts[amounts.length - 1];
      const bestOutput = best?.amounts[best.amounts.length - 1];
      if (output !== undefined && (bestOutput === undefined || output > bestOutput)) {
        best = { path: routeCandidates[index], amounts };
      }
    });
    return best;
  }, [routeCandidates, routeQuotes]);

  const path = bestQuote?.path;
  const amountsOut = bestQuote?.amounts;

  const toAmountWei = amountsOut ? amountsOut[amountsOut.length - 1] : undefined;
  const toAmount = toAmountWei ? formatUnits(toAmountWei, toDecimals) : '';

  // Price impact = how far the trade's execution rate sits below the marginal
  // (near-spot) rate. We approximate spot by quoting a tiny reference trade
  // (0.01% of the input) down the same path, then compare the two rates.
  const refAmountIn =
    amountInWei !== null ? (amountInWei / 10000n > 0n ? amountInWei / 10000n : 1n) : undefined;
  const { data: refAmountsOut } = useReadContract({
    address: deployment?.router,
    abi: ROUTER_ABI,
    functionName: 'getAmountsOut',
    args: refAmountIn && path ? [refAmountIn, path] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(refAmountIn && path && deployment && amountInWei) },
  });

  const priceImpact = useMemo(() => {
    if (amountInWei === null || !toAmountWei || !refAmountIn || !refAmountsOut) return null;
    const refOut = refAmountsOut[refAmountsOut.length - 1];
    if (refOut === 0n || toAmountWei === 0n) return null;
    const execRate =
      Number(formatUnits(toAmountWei, toDecimals)) / Number(formatUnits(amountInWei, fromDecimals));
    const spotRate =
      Number(formatUnits(refOut, toDecimals)) / Number(formatUnits(refAmountIn, fromDecimals));
    if (!Number.isFinite(execRate) || !Number.isFinite(spotRate) || spotRate === 0) return null;
    const impact = (1 - execRate / spotRate) * 100;
    return impact > 0 ? impact : 0;
  }, [amountInWei, toAmountWei, refAmountIn, refAmountsOut, toDecimals, fromDecimals]);

  // Three-tier severity: >1% notice, >3% high, >=5% critical (requires acknowledgement).
  const impactTier: 'warn' | 'high' | 'critical' | null =
    priceImpact === null
      ? null
      : priceImpact >= 5
        ? 'critical'
        : priceImpact >= 3
          ? 'high'
          : priceImpact >= 1
            ? 'warn'
            : null;
  const blockedByImpact = impactTier === 'critical' && !ackHighImpact;

  const needsApproval =
    !isFromNative && amountInWei !== null && (allowance === undefined || allowance < amountInWei);

  const {
    writeContract: writeApprove,
    data: approveHash,
    isPending: isApprovePending,
    reset: resetApprove,
  } = useWriteContract();
  const { isLoading: isApproveConfirming, isSuccess: isApproveConfirmed, error: approveReceiptError, refetch: retryApproveReceipt } = useWaitForTransactionReceipt({
    hash: approveHash,
    timeout: 300_000,
  });

  const {
    writeContract: writeSwap,
    data: swapHash,
    isPending: isSwapPending,
    reset: resetSwap,
  } = useWriteContract();
  const { isLoading: isSwapConfirming, isSuccess: isSwapConfirmed, error: swapReceiptError, refetch: retrySwapReceipt } = useWaitForTransactionReceipt({
    hash: swapHash,
    timeout: 300_000,
  });

  useEffect(() => {
    if (isApproveConfirmed) refetchAllowance();
  }, [isApproveConfirmed, refetchAllowance]);

  useEffect(() => {
    if (isSwapConfirmed && swapHash && address) {
      logTransaction({
        type: 'swap',
        hash: swapHash,
        account: address,
        chainId: readChainId,
        details: { fromToken, toToken, fromAmount, toAmount },
      });
      setFromAmount('');
      resetSwap();
    }
  }, [address, fromAmount, fromToken, isSwapConfirmed, readChainId, resetSwap, swapHash, toAmount, toToken]);

  const swapDirection = () => {
    setFromToken(toToken);
    setToToken(fromToken);
    setFromAmount('');
    setTxError(null);
    setAckHighImpact(false);
  };

  const handleApprove = () => {
    if (!fromAddress || !deployment) return;
    setTxError(null);
    writeApprove(
      {
        address: fromAddress,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [deployment.router, 2n ** 256n - 1n],
      },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const handleSwap = () => {
    if (!amountInWei || !path || !deployment || !address || !toAmountWei) return;
    setTxError(null);
    const amountOutMin = (toAmountWei * BigInt(Math.round((100 - slippage) * 100))) / 10000n;
    const deadline = BigInt(Math.floor(Date.now() / 1000) + DEADLINE_SECONDS);

    if (isFromNative) {
      writeSwap(
        {
          address: deployment.router,
          abi: ROUTER_ABI,
          functionName: 'swapExactBNBForTokens',
          args: [amountOutMin, path, address, deadline],
          value: amountInWei,
        },
        { onError: (err) => setTxError(err.message) },
      );
    } else if (isToNative) {
      writeSwap(
        {
          address: deployment.router,
          abi: ROUTER_ABI,
          functionName: 'swapExactTokensForBNB',
          args: [amountInWei, amountOutMin, path, address, deadline],
        },
        { onError: (err) => setTxError(err.message) },
      );
    } else {
      writeSwap(
        {
          address: deployment.router,
          abi: ROUTER_ABI,
          functionName: 'swapExactTokensForTokens',
          args: [amountInWei, amountOutMin, path, address, deadline],
        },
        { onError: (err) => setTxError(err.message) },
      );
    }
  };

  const rate = amountInWei && toAmountWei ? Number(toAmount) / Number(fromAmount) : null;

  const isPending = isApprovePending || isApproveConfirming || isSwapPending || isSwapConfirming;

  let buttonLabel = 'Enter an amount';
  let buttonAction: (() => void) | undefined;
  let buttonDisabled = true;

  if (!isConnected) {
    buttonLabel = 'Connect wallet to swap';
  } else if (!onCorrectChain) {
    buttonLabel = 'Switch network to swap';
  } else if (factoryPairs.unsupportedChain) {
    buttonLabel = 'Unsupported network';
  } else if (factoryPairs.loading) {
    buttonLabel = 'Discovering routes...';
  } else if (factoryPairs.error) {
    buttonLabel = 'Route discovery failed';
  } else if (routeCandidates.length === 0) {
    buttonLabel = 'No route available';
  } else if (amountInWei && !amountsOut) {
    buttonLabel = 'Fetching quote...';
  } else if (amountInWei) {
    if (isApprovePending || isApproveConfirming) {
      buttonLabel = 'Approving...';
    } else if (needsApproval) {
      buttonLabel = `Approve ${fromToken}`;
      buttonAction = handleApprove;
      buttonDisabled = false;
    } else if (isSwapPending || isSwapConfirming) {
      buttonLabel = 'Swapping...';
    } else {
      buttonLabel = blockedByImpact ? 'Confirm high price impact' : 'Swap';
      buttonAction = handleSwap;
      buttonDisabled = !toAmountWei || blockedByImpact;
    }
  }

  return (
    <div className="w-full lg:w-96 flex-shrink-0 border-t lg:border-t-0 lg:border-l border-abyss-700 bg-abyss-900/40 flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-abyss-700">
        <span className="text-sm font-semibold text-foam-100">Swap</span>
        <button
          onClick={() => setShowSettings((s) => !s)}
          className="text-foam-500 hover:text-foam-100 transition-colors"
        >
          <Settings size={16} />
        </button>
      </div>

      {showSettings && (
        <div className="mx-4 mt-3 p-3 rounded-lg border border-abyss-700 bg-abyss-850 text-xs">
          <div className="text-foam-500 mb-2">Slippage tolerance</div>
          <div className="flex items-center gap-2">
            {[0.1, 0.5, 1.0].map((v) => (
              <button
                key={v}
                onClick={() => {
                  setSlippage(v);
                  setCustomSlippage('');
                }}
                className={`px-2.5 h-8 rounded-md font-medium ${
                  slippage === v && customSlippage === ''
                    ? 'bg-tide-500 text-abyss-950'
                    : 'bg-abyss-700 text-foam-300 hover:bg-abyss-600'
                }`}
              >
                {v}%
              </button>
            ))}
            <div
              className={`flex items-center gap-1 px-2 h-8 rounded-md ${
                customSlippage !== '' ? 'bg-tide-500/20 ring-1 ring-tide-500' : 'bg-abyss-700'
              }`}
            >
              <input
                value={customSlippage}
                inputMode="decimal"
                placeholder="Custom"
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9.]/g, '');
                  setCustomSlippage(raw);
                  const n = Number(raw);
                  if (raw !== '' && !Number.isNaN(n) && n > 0 && n <= 50) setSlippage(n);
                }}
                className="w-14 bg-transparent text-foam-100 outline-none tabular-nums placeholder:text-foam-700"
              />
              <span className="text-foam-500">%</span>
            </div>
          </div>
          {slippage >= 5 && (
            <div className="mt-2 text-amber-400">High slippage — your trade may be front-run.</div>
          )}
        </div>
      )}

      <div className="p-4 space-y-1.5">
        <TokenInput
          label="From"
          token={fromToken}
          amount={fromAmount}
          balance={fromBalance ? Number(fromBalance).toFixed(4) : '0.00'}
          onAmountChange={(v) => {
            setFromAmount(v);
            resetApprove();
            setAckHighImpact(false);
          }}
          onTokenChange={setFromToken}
        />

        <div className="flex justify-center -my-1 relative z-10">
          <button
            onClick={swapDirection}
            aria-label="Switch swap direction"
            className="w-12 h-12 rounded-lg bg-abyss-800 border-4 border-abyss-900 flex items-center justify-center text-foam-300 hover:text-tide-400 hover:rotate-180 transition-all duration-300"
          >
            <ArrowDownUp size={16} />
          </button>
        </div>

        <TokenInput
          label="To (estimated)"
          token={toToken}
          amount={toAmount}
          balance={toBalance ? Number(toBalance).toFixed(4) : '0.00'}
          onTokenChange={setToToken}
        />

        {amountInWei && (
          <div className="pt-2 space-y-1.5 text-xs text-foam-500">
            {rate !== null && (
              <Row label="Rate" value={`1 ${fromToken} = ${rate.toFixed(6)} ${toToken}`} />
            )}
            {priceImpact !== null && (
              <div className="flex justify-between">
                <span>Price impact</span>
                <span
                  className={
                    impactTier === 'critical'
                      ? 'text-coral-400 font-semibold'
                      : impactTier === 'high'
                        ? 'text-amber-500 font-semibold'
                        : impactTier === 'warn'
                          ? 'text-amber-400'
                          : 'text-tide-400'
                  }
                >
                  {priceImpact < 0.01 ? '<0.01' : priceImpact.toFixed(2)}%
                </span>
              </div>
            )}
            {toAmountWei && (
              <Row
                label="Min. received"
                value={`${Number(
                  formatUnits(
                    (toAmountWei * BigInt(Math.round((100 - slippage) * 100))) / 10000n,
                    toDecimals,
                  ),
                ).toFixed(6)} ${toToken}`}
              />
            )}
            {path && path.length > 2 && (
              <Row
                label="Route"
                value={`${fromToken} → ${
                  path[1].toLowerCase() === deployment?.tokens.USDT?.toLowerCase() ? 'USDT' : 'BNB'
                } → ${toToken}`}
              />
            )}
            <Row label="Slippage tolerance" value={`${slippage}%`} />
            <Row label="Liquidity provider fee" value="0.30%" />
          </div>
        )}

        {amountInWei && (impactTier === 'high' || impactTier === 'critical') && (
          <div
            className={`mt-2 px-3 py-2 rounded-md text-xs flex items-start gap-2 ${
              impactTier === 'critical'
                ? 'bg-coral-500/15 text-coral-400'
                : 'bg-amber-500/15 text-amber-400'
            }`}
          >
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div className="space-y-1.5">
              <p>
                {impactTier === 'critical'
                  ? `Very high price impact (${priceImpact?.toFixed(2)}%). You may lose a significant portion of your funds to slippage.`
                  : `High price impact (${priceImpact?.toFixed(2)}%). Consider reducing the trade size.`}
              </p>
              {impactTier === 'critical' && (
                <label className="flex items-center gap-2 cursor-pointer select-none text-coral-400">
                  <input
                    type="checkbox"
                    checked={ackHighImpact}
                    onChange={(e) => setAckHighImpact(e.target.checked)}
                    className="accent-coral-500 w-4 h-4"
                  />
                  I understand and want to proceed anyway
                </label>
              )}
            </div>
          </div>
        )}

        <button
          onClick={buttonAction}
          disabled={buttonDisabled || isPending}
          className="w-full mt-3 py-3 rounded-xl font-semibold text-sm transition-colors disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed bg-tide-500 text-abyss-950 hover:bg-tide-400"
        >
          {buttonLabel}
        </button>

        {txError && (
          <div className="mt-2 px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">
            {txError}
          </div>
        )}

        {factoryPairs.error && (
          <div className="mt-2 px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">
            Pair discovery failed: {factoryPairs.error.message}
          </div>
        )}

        <TransactionTimeout attempts={[
          { error: approveReceiptError, hash: approveHash, retry: retryApproveReceipt },
          { error: swapReceiptError, hash: swapHash, retry: retrySwapReceipt },
        ]} />

        {isSwapConfirmed && !txError && (
          <div className="mt-2 px-3 py-2 rounded-md bg-tide-400/10 text-tide-400 text-xs">
            Swap confirmed on-chain.
          </div>
        )}
      </div>

      <div className="mt-auto p-4 border-t border-abyss-700 text-[11px] text-foam-500 leading-relaxed">
        Non-custodial swap. Your funds never leave your wallet — trades are executed
        directly against on-chain liquidity pools.
      </div>
      <div className="p-4 pt-0">
        <TrustBar contract={deployment?.router} />
      </div>
    </div>
  );
}

function TokenInput({
  label,
  token,
  amount,
  balance,
  onAmountChange,
  onTokenChange,
}: {
  label: string;
  token: string;
  amount: string;
  balance: string;
  onAmountChange?: (v: string) => void;
  onTokenChange: (v: string) => void;
}) {
  return (
    <div className="p-3 rounded-xl border border-abyss-700 bg-abyss-850">
      <div className="flex items-center justify-between text-xs text-foam-500 mb-2">
        <span>{label}</span>
        <span>Balance: {balance}</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          value={amount}
          inputMode="decimal"
          onChange={(e) => onAmountChange?.(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="0.0"
          readOnly={!onAmountChange}
          className="w-full bg-transparent text-2xl font-medium text-foam-100 outline-none placeholder:text-foam-700 tabular-nums"
        />
        <select
          value={token}
          onChange={(e) => onTokenChange(e.target.value)}
          className="flex items-center gap-1 pl-2 pr-1 py-1.5 rounded-lg bg-abyss-700 text-sm font-semibold text-foam-100 outline-none shrink-0 cursor-pointer"
        >
          {Object.keys(TOKENS).map((sym) => (
            <option key={sym} value={sym}>
              {sym}
            </option>
          ))}
        </select>
        <TokenIcon symbol={token} size={22} />
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span className="text-foam-300">{value}</span>
    </div>
  );
}
